import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { atom, useRecoilState } from 'recoil';

const Filter = styled.div`
  position: sticky;
  top: 3rem;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 3px;
  gap: 3px;
  width: 60px;
`;

const FilterItem = styled.div<{ isSelected: boolean }>`
  position: relative;
  width: 60px;
  height: 60px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  color: #fff;
  background-color: ${(props) =>
    props.isSelected ? '#ffffff5e' : '#ffffff26'};

  ${(props) =>
    props.isSelected
      ? `box-shadow: inset 0 0 0 1px hsl(0deg 0% 100% / 70%);`
      : ''}

  :after,
  :before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    pointer-events: none;
  }

  :before {
    width: 100%;
    height: 100%;
    transition: box-shadow 0.25s, background-color 0.25s;
    box-shadow: inset 0 0 0 1px hsl(0deg 0% 100% / 20%);
  }

  :after {
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    box-shadow: 0 0 0 2px hsl(0deg 0% 100% / 0%);
    transform: scale(var(--ui-scale)) translate3d(-2px, -2px, 0);
    transition: box-shadow 0.25s, transform 0.25s;
  }

  :hover {
    background-color: #fff6;
    z-index: 2;
  }

  :hover:before {
    box-shadow: inset 0 0 0 2px #fff;
    background-color: #fff3;
  }

  :hover:after {
    box-shadow: 0 0 0 2px hsl(0deg 0% 100% / 60%);
    transform: scale(1) translate3d(-2px, -2px, 0);
    z-index: 4;
  }

  img {
    max-width: 70%;
    height: auto;
  }
`;

const Tooltip = styled.div`
  position: fixed;
  display: grid;
  grid-auto-flow: column;
  align-items: start;
  align-content: start;
  grid-gap: 1rem;
  gap: 1rem;
  z-index: 2000;
  color: #fffc;
  font-size: 16px;
  line-height: 1;
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;
  opacity: 0;
  left: 0;
  top: 0;
`;

const TooltipWrapper = styled.div`
  box-shadow: 0 20px 60px rgb(0 0 0 / 90%);
  width: 350px;
`;

const TooltipHeader = styled.div`
  padding: 12px 16px 10px;
  background-color: #000000e6;
  border-top: 6px solid #ccc;
  color: #fff;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
  word-break: break-word;
  font-size: 20px;
`;

const TooltipBody = styled.div`
  padding: 12px 16px 10px;
  background-color: #050505d9;
  line-height: 1.4;
`;

type Faction = {
  name: string;
  icon: string;
  description: string;
  aliases?: string[];
};

const factions: Faction[] = [
  {
    name: 'Eagle Union',
    icon: 'faction-icons/eagle-union.png',
    description:
      'This nation concentrated their strength on developing aircraft carriers and powerful heavy cruisers, which grants them [the] upper hand when it comes to air superiority and open naval combat. Having repair ship[s] as their auxiliary allows [Eagle Union ships] regenerate health in battle.<br><br>Eagle Union represents the true spirit of freedom.',
  },
  {
    name: 'Royal Navy',
    icon: 'faction-icons/royal-navy.png',
    description:
      'When it comes to proper shipbuilding, Royal Navy is often ahead of their opponents due to their long and glorious history of navigation. The faith in her majesty the queen and rich battle experience helps them to build ships with perfectly balanced performance. If you wish for peace, prepare for war!',
  },
  {
    name: 'Sakura Empire',
    icon: 'faction-icons/sakura-empire.png',
    description:
      "Shrouded in mysterious fog and falling sakura petals, a beautiful island feigned sleep, silently, waiting for its turn to tremble the world. When the day came... the sky was darkened with its fighters, the ocean was reddened by its torpedoes, and mankind's fear... mankind's fear was defined by a new name - the Sakura Empire!",
  },
  {
    name: 'Iron Blood',
    icon: 'faction-icons/iron-blood.png',
    description:
      'There flows no soft blood in their vains, for they shall feel no mercy and no pain! Once a member of Azur Lane, these ruthless sharks have now allied themselves with the Sirens and decided to play with soft jellyfishes no more, knowing that peace and love are just for weaklings.<br><br>However, their betrayal is a necessity, for peace can never protect humanity from the unknown.<br><br>Hail Ironblood.',
  },
  {
    name: 'Dragon Empery',
    icon: 'faction-icons/dragon-empery.png',
    description:
      'A nation located on the Eastern continent known as the Land of the Gods. A core member of the Azur Lane, they take part in the battle against the Sirens and the Crimson Axis.',
  },
  {
    name: 'Northern Parliament',
    icon: 'faction-icons/northern-parliament.png',
    aliases: ['North Union'],
    description:
      "The Northern Parliament was built upon collectivistic ideals, the source of her peoples' strength and solidarity. Their fleet has not been active within the Azur Lane for some time now, with some reports claiming that they are tied up with the Sirens in the polar regions.",
  },
  {
    name: 'Iris Libre',
    icon: 'faction-icons/iris-libre.png',
    description:
      'When the former Iris Orthodoxy collapsed, some of their naval forces, the Templar Knights, joined the Azur Lane in hopes of rebuilding their fallen country. The newly-formed Iris Libre now stands opposed to both the Vichya Dominion and Iron Blood.',
  },
  {
    name: 'Sardegna Empire',
    icon: 'faction-icons/sardegna-empire.png',
    description:
      'Boasting a profound history and magnificent feats of art, Sardegna has much more to be proud of than just its military might. The military organization that controls the navy is the Imperial Guard.<br><br>Though the voices within its governing Senate are not unified, Sardegna has chosen to become a core member of the Crimson Axis, distancing itself from the Azur Lane.',
  },
  {
    name: 'Vichya Dominion',
    icon: 'faction-icons/vichya-dominion.png',
    description:
      "A faction born out of the collapse of the Iris Orthodoxy, the Vichya Dominion, was once part of [the] Iris's Templar Knights. They have since joined with the Crimson Axis.",
  },
];

export function FactionFilter() {
  const [isVisible, setIsVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [selected, setSelected] = useState(factions[0]);
  const [activeFaction, setActiveFaction] = useRecoilState(factionFilterState);

  function handleMouseEnter(faction: any) {
    setSelected(faction);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    setIsVisible(true);
    setPos({ x: e.clientX, y: e.clientY });
  }

  function handleMouseLeave(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    setIsVisible(false);
  }

  return (
    <>
      <Tooltip
        style={{
          opacity: isVisible ? 1 : 0,
          transform: `translate(${pos.x + 30}px, ${pos.y}px)`,
        }}
      >
        <TooltipWrapper>
          <TooltipHeader>{selected.name}</TooltipHeader>
          <TooltipBody
            dangerouslySetInnerHTML={{ __html: selected.description || '-' }}
          />
        </TooltipWrapper>
      </Tooltip>

      <Filter>
        <FilterItem
          isSelected={activeFaction === null}
          onClick={() => setActiveFaction(null)}
        >
          <img src="faction-icons/all.png" alt="" />
        </FilterItem>

        {factions.map((faction) => (
          <FilterItem
            key={faction.name}
            onMouseEnter={() => handleMouseEnter(faction)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            isSelected={activeFaction?.name === faction.name}
            onClick={() => setActiveFaction(faction)}
          >
            <img src={faction.icon} alt="" />
          </FilterItem>
        ))}
      </Filter>
    </>
  );
}

export const factionFilterState = atom<Faction | null>({
  key: 'factionFilterState',
  default: null,
});
